import { useRef, useMemo } from "react";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { get } from "../../utils/helpers/api";
import SelectCustomStyles from "../../utils/styles/reactSelect";

const resolveLabel = (item) => {
  if (item.name) return item.name;
  if (item.company_name) return item.company_name;
  if (item.firstname || item.lastname)
    return [item.firstname, item.lastname].filter(Boolean).join(' ');
  if (item.title) return item.title;
  return String(item.id);
};

function CustomSelect({
  value,
  onChange,
  module,
  responseKey,
  canCreate,
  placeholder,
  createLink
}) {
  const { t } = useTranslation();
  const debounceRef = useRef(null);

  const resolvedValue =
    value == null
      ? null
      : typeof value === "object"
      ? value
      : { value, label: String(value) };

  const selectComponents = useMemo(() => ({
    Menu: ({ children, ...props }) => (
      <components.Menu {...props}>
        {children}
        {createLink && (
          <div style={{ padding: '8px 12px', borderTop: '1px solid #dee2e6' }} className="text-center">
            <Link
              to={createLink}
              style={{ fontSize: '0.875rem', color: '#0d6efd', textDecoration: 'none' }}
            >
              {t('common.createNew')}
            </Link>
          </div>
        )}
      </components.Menu>
    ),
  }), [createLink, t]);

  const loadOptions = (inputValue, callback) => {
    if (inputValue.length === 1) {
      callback([]);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const query = inputValue.length >= 2
          ? `/${module}?search=${encodeURIComponent(inputValue)}&page_size=10`
          : `/${module}?page_size=10`;

        const data = await get(query);
        const moduleKey = responseKey || module.split('/').pop();
        const items = data?.[moduleKey]?.data ?? data?.data ?? [];

        const options = items.map((item) => ({
          value: item.id,
          label: resolveLabel(item),
          objectData: item
        }));

        const hasExact = options.some(
          (opt) => opt.label.toLowerCase() === inputValue.toLowerCase()
        );

        if (!hasExact && canCreate && inputValue.length >= 2) {
          options.unshift({ value: inputValue, label: inputValue, isTyped: true });
        }

        callback(options);
      } catch {
        callback([]);
      }
    }, 300);
  };

  return (
    <AsyncSelect
      value={resolvedValue}
      onChange={(selected) => onChange(selected, selected?.objectData)}
      loadOptions={loadOptions}
      defaultOptions={true}
      cacheOptions={false}
      placeholder={placeholder ?? t('common.searchDots')}
      isClearable
      styles={SelectCustomStyles}
      components={selectComponents}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length === 1
          ? t('common.typeMinChars')
          : t('common.noResultsFound')
      }
      loadingMessage={() => t('common.searchingDots')}
    />
  );
}

export default CustomSelect;
